import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TourCard = ({ tour, viewMode = 'list' }) => {
  const [isWishlisted, setIsWishlisted] = useState(tour?.isWishlisted || false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();

  const handleWishlistToggle = (e) => {
    e?.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };

  const handleViewDetails = () => {
    navigate(`/tour-detail?id=${tour?.id}`);
  };

  const handleBookNow = (e) => {
    e?.stopPropagation();
    navigate(`/booking-flow?tourId=${tour?.id}`);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'text-green-600 bg-green-50';
      case 'moderate': return 'text-yellow-600 bg-yellow-50';
      case 'challenging': return 'text-orange-600 bg-orange-50';
      case 'extreme': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(<Icon key={i} name="Star" size={14} className="text-yellow-400 fill-current" />);
    }

    if (hasHalfStar) {
      stars?.push(<Icon key="half" name="StarHalf" size={14} className="text-yellow-400 fill-current" />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(<Icon key={`empty-${i}`} name="Star" size={14} className="text-gray-300" />);
    }

    return stars;
  };

  if (viewMode === 'grid') {
    return (
      <div className="bg-white rounded-xl shadow-card hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer hover-lift">
        <div className="relative" onClick={handleViewDetails}>
          {/* Image */}
          <div className="relative h-48 overflow-hidden">
            <Image
              src={tour?.image}
              alt={tour?.title}
              className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 bg-muted animate-pulse" />
            )}
            
            {/* Wishlist Button */}
            <button
              onClick={handleWishlistToggle}
              className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white transition-colors shadow-sm"
            >
              <Icon 
                name="Heart" 
                size={18} 
                className={isWishlisted ? 'text-red-500 fill-current' : 'text-gray-600'} 
              />
            </button>

            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col space-y-1">
              {tour?.isInstantBooking && (
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-md font-medium">
                  Instant Booking
                </span>
              )}
              {tour?.discount && (
                <span className="bg-accent text-white text-xs px-2 py-1 rounded-md font-medium">
                  {tour?.discount}% OFF
                </span>
              )}
            </div>

            {/* Availability Indicator */}
            <div className="absolute bottom-3 left-3">
              <span className={`text-xs px-2 py-1 rounded-md font-medium ${
                tour?.availability === 'high' ? 'bg-green-100 text-green-800' :
                tour?.availability === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
              }`}>
                {tour?.availability === 'high' ? 'High Availability' :
                 tour?.availability === 'medium'? 'Limited Spots' : 'Few Spots Left'}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Category & Difficulty */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-primary font-medium">{tour?.category}</span>
              {tour?.difficulty && (
                <span className={`text-xs px-2 py-1 rounded-md font-medium ${getDifficultyColor(tour?.difficulty)}`}>
                  {tour?.difficulty}
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
              {tour?.title}
            </h3>

            {/* Location */}
            <div className="flex items-center text-muted-foreground mb-3">
              <Icon name="MapPin" size={14} className="mr-1" />
              <span className="text-sm">{tour?.location}</span>
            </div>

            {/* Rating & Reviews */}
            <div className="flex items-center mb-3">
              <div className="flex items-center mr-2">
                {renderStars(tour?.rating)}
              </div>
              <span className="text-sm font-medium text-foreground">{tour?.rating}</span>
              <span className="text-sm text-muted-foreground ml-1">({tour?.reviewCount} reviews)</span>
            </div>

            {/* Duration & Group Size */}
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
              <div className="flex items-center">
                <Icon name="Clock" size={14} className="mr-1" />
                <span>{tour?.duration}</span>
              </div>
              <div className="flex items-center">
                <Icon name="Users" size={14} className="mr-1" />
                <span>Max {tour?.maxGroupSize}</span>
              </div>
            </div>

            {/* Price & CTA */}
            <div className="flex items-center justify-between">
              <div>
                {tour?.originalPrice && tour?.originalPrice > tour?.price && (
                  <span className="text-sm text-muted-foreground line-through mr-2">
                    ${tour?.originalPrice}
                  </span>
                )}
                <span className="text-lg font-bold text-foreground">
                  ${tour?.price}
                </span>
                <span className="text-sm text-muted-foreground ml-1">per person</span>
              </div>
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="p-4 pt-0 flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewDetails}
            className="flex-1"
          >
            View Details
          </Button>
          <Button
            size="sm"
            onClick={handleBookNow}
            className="flex-1"
          >
            Book Now
          </Button>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="bg-white rounded-xl shadow-card hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer hover-lift">
      <div className="flex flex-col md:flex-row" onClick={handleViewDetails}>
        {/* Image */}
        <div className="relative md:w-80 h-48 md:h-auto overflow-hidden">
          <Image
            src={tour?.image}
            alt={tour?.title}
            className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse" />
          )}
          
          {/* Wishlist Button */}
          <button
            onClick={handleWishlistToggle}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white transition-colors shadow-sm"
          >
            <Icon 
              name="Heart" 
              size={18} 
              className={isWishlisted ? 'text-red-500 fill-current' : 'text-gray-600'} 
            />
          </button>

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col space-y-1">
            {tour?.isInstantBooking && (
              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-md font-medium">
                Instant Booking
              </span>
            )}
            {tour?.discount && (
              <span className="bg-accent text-white text-xs px-2 py-1 rounded-md font-medium">
                {tour?.discount}% OFF
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm text-primary font-medium">{tour?.category}</span>
                  {tour?.difficulty && (
                    <span className={`text-xs px-2 py-1 rounded-md font-medium ${getDifficultyColor(tour?.difficulty)}`}>
                      {tour?.difficulty}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {tour?.title}
                </h3>
                <div className="flex items-center text-muted-foreground mb-3">
                  <Icon name="MapPin" size={16} className="mr-1" />
                  <span>{tour?.location}</span>
                </div>
              </div>
              
              {/* Availability */}
              <span className={`text-xs px-2 py-1 rounded-md font-medium ${
                tour?.availability === 'high' ? 'bg-green-100 text-green-800' :
                tour?.availability === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
              }`}>
                {tour?.availability === 'high' ? 'High Availability' :
                 tour?.availability === 'medium'? 'Limited Spots' : 'Few Spots Left'}
              </span>
            </div>

            {/* Description */}
            <p className="text-muted-foreground mb-4 line-clamp-2">
              {tour?.description}
            </p>

            {/* Features */}
            <div className="flex flex-wrap gap-2 mb-4">
              {tour?.features?.slice(0, 3)?.map((feature, index) => (
                <span key={index} className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-md">
                  {feature}
                </span>
              ))}
              {tour?.features?.length > 3 && (
                <span className="text-xs text-primary">+{tour?.features?.length - 3} more</span>
              )}
            </div>

            {/* Bottom Section */}
            <div className="flex items-end justify-between mt-auto">
              {/* Left Side - Rating & Details */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <div className="flex items-center mr-2">
                    {renderStars(tour?.rating)}
                  </div>
                  <span className="font-medium text-foreground">{tour?.rating}</span>
                  <span className="text-sm text-muted-foreground ml-1">({tour?.reviewCount})</span>
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center">
                    <Icon name="Clock" size={14} className="mr-1" />
                    <span>{tour?.duration}</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Users" size={14} className="mr-1" />
                    <span>Max {tour?.maxGroupSize}</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Price & Actions */}
              <div className="text-right">
                <div className="mb-3">
                  {tour?.originalPrice && tour?.originalPrice > tour?.price && (
                    <div className="text-sm text-muted-foreground line-through">
                      ${tour?.originalPrice}
                    </div>
                  )}
                  <div className="text-2xl font-bold text-foreground">
                    ${tour?.price}
                  </div>
                  <div className="text-sm text-muted-foreground">per person</div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleViewDetails}
                  >
                    View Details
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleBookNow}
                  >
                    Book Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourCard;